import {
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenTrialExtensionModalAtom,
  trialExtensionModalOpenAtom,
} from '@/atoms/freeTrialModalAtoms';
import { AppleIapInfoText } from '@/components/apple-iap-info-text';
import {
  TRIAL_EXTENSION_OVERLAY_CLASS,
  TRIAL_EXTENSION_TIER_MODAL_CLASS,
} from '@/components/modal/trial-extension/constants';
import { TrialExtensionTaskRow } from '@/components/modal/trial-extension/TrialExtensionTaskRow';
import { TrialExtensionTraineeVerificationBanner } from '@/components/modal/trial-extension/TrialExtensionTraineeVerificationBanner';
import { useIsHeidi2Clinician } from '@/components/modal/trial-extension/use-is-heidi-2-clinician';
import { useIsTraineeSpecialty } from '@/components/modal/useIsTraineeSpecialty';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { useIsHeidi2FullUpgradeFlowEnabled } from '@/features/flags';
import { pricingPlansModalOpenAtom } from '@/features/settings/billing/billingAtoms';
import { useSubscriptionUpgrade } from '@/features/settings/billing/hooks/useSubscriptionUpgrade';
import { useManualExposure } from '@/hooks/amplitude';
import { useQueryGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import {
  getModalFooter,
  getModalSubtitle,
  getModalTitle,
  getTrialExtensionProgress,
} from '@/hooks/trialExtensionProgress';
import { trialExtensionProgressMessages } from '@/hooks/trialExtensionProgress.messages';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useTrialExtensionModalHandlers } from '@/hooks/useTrialExtensionModalHandlers';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { Period } from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierProFilled } from '@heidi/design-system/icons';
import { Progress } from '@heidi/design-system/progress';
import { Separator } from '@heidi/design-system/separator';
import {
  TypographyFootNote,
  TypographyH2,
  TypographyP2,
} from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import { Gift } from 'lucide-react';
import { Fragment, useEffect } from 'react';
import { useIntl } from 'react-intl';

type TierModalConfig = {
  featureFlagKey: string;
  checkoutTier: SubscriptionTier;
  analyticsAction: ButtonAction;
  translations: {
    earnedTitle: TranslationKey;
    earnTitle: TranslationKey;
    earnedSubtitle: TranslationKey;
    subtitle: TranslationKey;
  };
};

const TIER_MODAL_CONFIG: Partial<Record<SubscriptionTier, TierModalConfig>> = {
  [SubscriptionTier.CLINICIAN_PRO]: {
    featureFlagKey: 'release-gamify-trial-length-clinician',
    checkoutTier: SubscriptionTier.CLINICIAN_PRO,
    analyticsAction: ButtonAction.TrialExtensionUpgradeToClinicianPro,
    translations: {
      earnedTitle: 'trialExtension.earnedMoreDaysClinician',
      earnTitle: 'trialExtension.earnMoreDaysClinician',
      earnedSubtitle: 'trialExtension.earnedSubtitleClinician',
      subtitle: 'trialExtension.subtitleClinician',
    },
  },
  [SubscriptionTier.EVIDENCE]: {
    featureFlagKey: 'release-gamify-trial-length-evidence',
    checkoutTier: SubscriptionTier.EVIDENCE,
    analyticsAction: ButtonAction.TrialExtensionUpgradeToEvidence,
    translations: {
      earnedTitle: 'trialExtension.earnedMoreDaysEvidence',
      earnTitle: 'trialExtension.earnMoreDaysEvidence',
      earnedSubtitle: 'trialExtension.earnedSubtitleEvidence',
      subtitle: 'trialExtension.subtitleEvidence',
    },
  },
};

/**
 * Config-driven trial extension modal (new design from Figma).
 * Handles any non-Practice tier via TIER_MODAL_CONFIG lookup.
 * For Practice tier use GamifyTrialExtensionPracticeModal instead.
 */
export function GamifyTrialExtensionTierModal() {
  const [isOpen, setIsOpen] = useAtom(trialExtensionModalOpenAtom);
  const setEarnDaysNavPermanentlyDismissed = useSetAtom(
    earnDaysNavPermanentlyDismissedAtom
  );
  const setHasSeenTrialExtensionModal = useSetAtom(hasSeenTrialExtensionModalAtom);
  const { data, isLoading } = useQueryGamifyTrialExtension(isOpen);
  const { trialDaysLeft, evidenceTrialDaysLeft } = useReverseTrial();
  const isEvidenceTier = data?.tier === SubscriptionTier.EVIDENCE;
  const effectiveTrialDaysLeft = isEvidenceTier ? evidenceTrialDaysLeft : trialDaysLeft;
  const { t } = useTranslations();
  const intl = useIntl();
  const isHeidi2Clinician = useIsHeidi2Clinician(data?.tier);
  const isHeidi2FullUpgradeFlowEnabled = useIsHeidi2FullUpgradeFlowEnabled();
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const { handleUpgrade: handleSubscriptionUpgrade, isPending: isCreatingCheckout } =
    useSubscriptionUpgrade({
      onCheckoutComplete: () => {},
      freeTrialDays: effectiveTrialDaysLeft,
      onCheckoutError: () => setIsOpen(true),
    });

  const tierConfig = data?.tier
    ? TIER_MODAL_CONFIG[data.tier as SubscriptionTier]
    : undefined;

  const isTraineeSpecialty = useIsTraineeSpecialty(isOpen);

  useManualExposure({
    flagKey: tierConfig?.featureFlagKey ?? '',
    isEnabled: true,
    isVisible: isOpen && !!data?.eligible && !!tierConfig,
  });

  useEffect(() => {
    if (isOpen) setHasSeenTrialExtensionModal(true);
  }, [isOpen, setHasSeenTrialExtensionModal]);

  const { handleClose, handleCloseAutoFocus, getTaskHandler, handleTaskClick } =
    useTrialExtensionModalHandlers(isOpen, data, setIsOpen);

  const handleUpgrade = () => {
    if (!tierConfig) return;
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionModal,
      action: tierConfig.analyticsAction,
      label: 'Upgrade now',
    });
    handleClose();
    if (isHeidi2Clinician && isHeidi2FullUpgradeFlowEnabled) {
      setPricingPlansModalOpen(true);
      return;
    }
    void handleSubscriptionUpgrade(Period.ONE_YEAR, tierConfig.checkoutTier);
  };

  if (!data || !tierConfig || !(data.eligible || data.trial_extended)) {
    return null;
  }

  const progress = getTrialExtensionProgress(data);
  const { allTasksComplete, progressPercent } = progress;
  const showTraineeVerificationCta =
    !allTasksComplete &&
    isTraineeSpecialty &&
    data.tier === SubscriptionTier.CLINICIAN_PRO;

  const tierLabel =
    data.tier === SubscriptionTier.CLINICIAN_PRO
      ? t('trialExtension.tierNameClinician')
      : t('trialExtension.tierNameEvidence');

  const earnTitle = isHeidi2Clinician
    ? intl.formatMessage(trialExtensionProgressMessages.modalTitleEarnHeidi2Clinician, {
        days: data.trial_extension_days,
      })
    : t(tierConfig.translations.earnTitle, { days: data.trial_extension_days });
  const heidi2CopyOptions = { isHeidi2Clinician, days: data.trial_extension_days };
  const title = getModalTitle(progress, t, earnTitle, intl, heidi2CopyOptions);
  const subtitle = getModalSubtitle(progress, t, intl, tierLabel, heidi2CopyOptions);
  const footerText = getModalFooter(progress, t, tierLabel, intl, heidi2CopyOptions);
  const upgradeLabel = isHeidi2Clinician
    ? intl.formatMessage(trialExtensionProgressMessages.modalUpgradeCtaHeidi2Clinician)
    : t('trialExtension.keepPlanAfterTrial', { plan: tierLabel });

  return (
    <DialogDrawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (allTasksComplete) {
            setEarnDaysNavPermanentlyDismissed(true);
          }
          handleClose();
        }
      }}
    >
      <DialogDrawerContent
        className={TRIAL_EXTENSION_TIER_MODAL_CLASS}
        overlayClassName={TRIAL_EXTENSION_OVERLAY_CLASS}
        onCloseIconClick={handleClose}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <div className="flex flex-col gap-6 p-8">
          <div className="flex justify-center">
            <FramedIcon state="pro" icon={<Gift />} />
          </div>

          <div className="flex flex-col">
            <TypographyH2 className="text-center">{title}</TypographyH2>
            <TypographyP2 className="mt-1 text-center text-text-secondary">
              {subtitle}
            </TypographyP2>
          </div>

          <Progress
            value={allTasksComplete ? 100 : progressPercent}
            className="h-2 w-full"
          />

          <div className="flex flex-col">
            {isLoading ? (
              <div className="py-8 text-center text-text-secondary">
                {t('trialExtension.loading')}
              </div>
            ) : (
              data.tasks?.map((task, index) => (
                <Fragment key={task.id}>
                  {index > 0 && <Separator className="mx-2 w-auto" />}
                  <TrialExtensionTaskRow
                    task={task}
                    variant="tier"
                    onAction={getTaskHandler(task.id)}
                    onTaskClick={handleTaskClick}
                  />
                </Fragment>
              ))
            )}
          </div>
          {showTraineeVerificationCta && (
            <TrialExtensionTraineeVerificationBanner
              trialDaysRemaining={effectiveTrialDaysLeft}
            />
          )}
          <div className="mt-6 flex flex-col gap-2">
            {allTasksComplete && !isAppleCompliantBuild() && (
              <Button
                size="lg"
                variant="pro"
                className="w-full"
                onClick={handleUpgrade}
                disabled={isCreatingCheckout}
              >
                <TierProFilled size={16} />
                {isCreatingCheckout ? t('trialToPaidModal.redirecting') : upgradeLabel}
              </Button>
            )}
            {allTasksComplete && isAppleCompliantBuild() && (
              <AppleIapInfoText className="text-center text-text-secondary" />
            )}
            <TypographyFootNote className="text-center text-text-secondary">
              {footerText}
            </TypographyFootNote>
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
